import { DeliveryProofBand } from '@/components/deliveries';
import { Seo, StructuredData } from '@/components/molecules';
import { CompanyStoryBand, ImportPromiseBand, PageIntro, QuoteBand } from '@/components/organisms';
import { introVehicles } from '@/config/introVehicles';
import { type ClientSector } from '@/content/clients';
import { type WhatsAppRouteHandle } from '@/hooks/usePageWhatsAppMessage';
import { breadcrumbSchema, organizationSchema } from '@/utils/structuredData';

import {
  clientCount,
  crumbs,
  introAction,
  introHighlights,
  sectorCount,
  WHATSAPP_MESSAGE,
} from './content';
import { useClientRoster } from './hooks/useClientRoster';
import { useFleetEnquiryForm } from './hooks/useFleetEnquiryForm';
import { ClientRosterBand, FleetQuoteBand } from './organisms';

/** Opens WhatsApp with the context of this page (§60 K/L). */
export const handle: WhatsAppRouteHandle = { whatsappMessage: WHATSAPP_MESSAGE };

/**
 * Our Clients — the page the home page's logo wall now links to.
 *
 * It exists because the roster outgrew the band that used to hold it. Seventeen
 * logos fit in two counter-scrolling rows; the whole roster does not, and the
 * fix is not
 * a third row. A marquee is read in a glance and cannot be scanned, searched or
 * cited, which are the three things the visitor who cares about this list wants
 * to do with it — a procurement officer, a fleet manager, or anyone deciding
 * whether an importer is safe to hand a purchase order to. `ClientsMarquee`
 * keeps the glance; this page takes the scanning.
 *
 * The order is that reader's own sequence of questions: who are you (the intro
 * and the company record), who have you supplied (the roster, which is the
 * page), does anything actually arrive (the handover archive), and who do I
 * speak to (the quotation band).
 *
 * ## The page used to stop at the proof
 *
 * Every band on it answered "are you real" and none of them asked the question
 * that follows. The only ask was the site's standard quotation band, three
 * bands below the roster, pointing at `/quote` — a form written for someone
 * buying one car for themselves, with nowhere to put an organisation's name or
 * a number of vehicles. So the page now carries its own conversion point,
 * `FleetQuoteBand`, immediately under the roster, and three things lead to it:
 * the opening band's button for a skimmer, the sector card at the foot of the
 * roster for a reader who has just found their own sector, and
 * `/clients#fleet-quote` from the home page's client wall.
 *
 * The closing `QuoteBand` is kept and re-pointed at the *other* reader — one
 * car, bought personally — so the page has one primary path and one
 * alternative rather than two identical asks.
 *
 * ## What state lives here, and why
 *
 * Two hooks, both called once and handed down. The roster's filter is read by
 * the band that draws it and by the sector card inside it; the enquiry form's
 * state has to be reachable from that card, because tapping it prefills the
 * sector before the anchor scrolls. Neither hook's logic is here — this file
 * wires them together and lays the bands out, and every count on the page is
 * still arithmetic on `content/clients.ts` rather than a number typed into
 * copy (see `./content.ts`).
 */
export default function ClientsPage() {
  const roster = useClientRoster();
  const fleetForm = useFleetEnquiryForm();

  /* The sector card's tap. The anchor beside it does the scrolling, so all
     this does is carry the reader's declared sector into the form; a tap made
     with no filter on leaves the picker for them to answer. */
  const onRequestQuote = (sector: ClientSector | null) => {
    if (sector) fleetForm.prefillSector(sector);
  };

  return (
    <>
      <StructuredData
        data={[organizationSchema(), breadcrumbSchema([{ label: 'Home', to: '/' }, ...crumbs])]}
      />
      <Seo
        title="Our Clients"
        description={`The ${clientCount} ministries, banks, universities, hospitals, energy projects and companies across ${sectorCount} sectors that import their vehicles through Kabex Motors.`}
        path="/clients"
      />

      <PageIntro
        eyebrow="Our clients"
        title="Trusted by Uganda's institutions and industry"
        lede="These are the organisations that have bought vehicles through us — government ministries, banks, universities, hospitals and private companies, each one imported and cleared the same way yours would be."
        crumbs={crumbs}
        slideshow
        highlights={introHighlights}
        action={introAction}
        whatsapp
      />

      {/* The page, first. Everything under it is context for a reader who has
          already found what they came for, and nothing should stand between
          the headline claim and the list that proves it. */}
      <ClientRosterBand roster={roster} onRequestQuote={onRequestQuote} />

      {/* The ask, directly under the proof. It is the page's primary path and
          the one band here that takes an answer rather than making a claim. */}
      <FleetQuoteBand form={fleetForm} />

      {/* Who the company behind that list is. The compact form, linking back to
          /about, as five other pages render it — a reader convinced by the
          roster asks about the company next, and this is the band written for
          that moment.

          `muted` because the enquiry band above it is now the default tone;
          the run down the page still alternates, and the archive below keeps
          the default it had. */}
      <CompanyStoryBand
        density="compact"
        titleId="clients-story-title"
        tone="muted"
        pattern="isometric"
        eyebrow="About Kabex Motors"
        title="The company those organisations bought from"
        lede="Established 2018, incorporated, with an office in Kampala a buyer can walk into."
        backdrop={{ picture: introVehicles['land-cruiser'].image, placement: 'right' }}
      />

      {/* A roster is a claim about who signed; this is the claim about what was
          delivered. It draws nothing until there is an archive. */}
      <DeliveryProofBand
        id="clients-delivery-proof"
        title="Cars we have handed over"
        lede="The orders behind the names above, as they actually finish."
      />

      <ImportPromiseBand />

      {/* Re-pointed at the reader this page is not written for: someone buying
          a single car for themselves, who arrived here anyway. The
          organisation's path is the form above, so this band must not repeat
          it — two identical asks on one page split the attention they were
          both meant to catch. */}
      <QuoteBand
        title="Buying a car for yourself?"
        text="The quotation form for a personal purchase asks for the car and your budget, and comes back with the shipping and the URA taxes set out separately."
        whatsappMessage="Hello Kabex Motors, I would like a quotation for a car."
      />
    </>
  );
}
